import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import CommentComposer from "@/components/social/CommentComposer";
import { ThumbsUp, ThumbsDown, MessageCircle, Share2, MapPin, Plane, MoreHorizontal, ShieldCheck, Trash2, CheckCircle2, Pencil, X as XIcon, Check as CheckIcon, Loader2 } from "lucide-react";

const TRIP_LABEL = {
  preflight: "Preflight",
  enroute:   "Enroute",
  landed:    "Just Landed",
  on_ground: "On Ground",
  aog:       "AOG",
};

const TRIP_TONE = {
  preflight: "text-sky border-sky/40 bg-sky/10",
  enroute:   "text-avail border-avail/40 bg-avail/10",
  landed:    "text-avail border-avail/40 bg-avail/10",
  on_ground: "text-white/70 border-white/20 bg-white/[0.04]",
  aog:       "text-[#EB5757] border-[#EB5757]/40 bg-[#EB5757]/10",
};

function relTime(iso) {
  try {
    const d = new Date(iso);
    const s = Math.floor((Date.now() - d.getTime()) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    if (s < 604800) return `${Math.floor(s / 86400)}d`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

function linkifyText(text) {
  if (!text) return null;
  // Order matters — extract mention tokens FIRST so their brackets/parens don't
  // get chewed up by hashtag or handle regex. Tokens: `@[Display Name](user:UUID)`.
  const USER_TOKEN = /@\[([^\]]+)\]\(user:([a-f0-9-]{8,})\)/g;
  const nodes = [];
  let cursor = 0;
  let match;
  let key = 0;
  while ((match = USER_TOKEN.exec(text)) !== null) {
    if (match.index > cursor) nodes.push(text.slice(cursor, match.index));
    const [, display, uid] = match;
    nodes.push(
      <Link
        key={`um-${key++}-${uid}`}
        to={`/crew/${uid}`}
        className="text-cyan font-semibold hover:underline"
        data-testid={`post-user-mention-${uid}`}
      >
        @{display}
      </Link>
    );
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) nodes.push(text.slice(cursor));

  // Second pass — split remaining plain-text nodes on whitespace and linkify
  // hashtags + FBO handles (legacy @-mentions).
  const out = [];
  nodes.forEach((node, ni) => {
    if (typeof node !== "string") { out.push(node); return; }
    const parts = node.split(/(\s+)/);
    parts.forEach((chunk, i) => {
      if (chunk.startsWith("#") && chunk.length > 1) {
        const tag = chunk.slice(1).replace(/[^A-Za-z0-9_]/g, "");
        if (tag) {
          out.push(
            <Link key={`h-${ni}-${i}`} to={`/feed?tag=${tag.toLowerCase()}`} className="text-sky hover:underline">
              #{tag}
            </Link>
          );
          return;
        }
      }
      if (chunk.startsWith("@") && chunk.length > 1) {
        const handle = chunk.slice(1).replace(/[^A-Za-z0-9_]/g, "");
        if (handle) {
          out.push(
            <Link key={`f-${ni}-${i}`} to={`/fbo/${handle}`} className="text-sky font-semibold hover:underline">
              @{handle}
            </Link>
          );
          return;
        }
      }
      out.push(<span key={`t-${ni}-${i}`}>{chunk}</span>);
    });
  });
  return out;
}

export default function PostCard({ post, onChange, onDeleted }) {
  const { user } = useAuth();
  const [state, setState] = useState(post);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [reacting, setReacting] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(post.text || "");
  const [savingEdit, setSavingEdit] = useState(false);

  const p = state;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const abs = (u) => (u && u.startsWith("/") ? `${backendUrl}${u}` : u);
  const author = p.author || {};

  // Role-based accent color — every post gets a color that pops against the
  // dark HUD background, plus corner brackets and a top edge glow.
  const ROLE_ACCENTS = {
    pilot:            { c: "#4B9CFF", glow: "rgba(75,156,255,0.35)" },  // blue
    flight_attendant: { c: "#FF6B9D", glow: "rgba(255,107,157,0.35)" }, // pink
    amt:              { c: "#F4C443", glow: "rgba(244,196,67,0.35)" },  // amber
    mechanic:         { c: "#F4C443", glow: "rgba(244,196,67,0.35)" },
    charter_operator: { c: "#2ED573", glow: "rgba(46,213,115,0.35)" },  // green
    admin:            { c: "#FFD86B", glow: "rgba(255,216,107,0.4)" },  // gold
    charter_client:   { c: "#C99BFF", glow: "rgba(201,155,255,0.35)" }, // purple
    empty_leg_rider:  { c: "#5FE895", glow: "rgba(95,232,149,0.35)" },  // teal
  };
  const accent = ROLE_ACCENTS[author.role] || ROLE_ACCENTS[author.membership_tier_slug] || { c: "#C99BFF", glow: "rgba(201,155,255,0.30)" };
  const isAOG = (p.trip_status === "aog") || /\baog\b/i.test(p.text || "");
  const finalAccent = isAOG ? { c: "#FF5555", glow: "rgba(255,85,85,0.55)" } : accent;

  const react = async (kind, emoji) => {
    if (reacting) return;
    setReacting(true);
    // Optimistic — flip the local state immediately so the reaction picker
    // feels instant. Backend still counts a plain like/dislike; the specific
    // emoji picked lives client-side on the post for now.
    const already = p.my_reaction === kind && (!emoji || p.my_reaction_emoji === emoji);
    setState((prev) => ({
      ...prev,
      my_reaction: already ? null : kind,
      my_reaction_emoji: already ? null : (emoji || (kind === "like" ? "👍" : "👎")),
    }));
    try {
      const r = await api.post(`/social/posts/${p.id}/react`, { kind });
      const next = {
        ...p,
        likes_count: r.data.likes_count,
        dislikes_count: r.data.dislikes_count,
        my_reaction: r.data.my_reaction,
        my_reaction_emoji: r.data.my_reaction ? (emoji || (r.data.my_reaction === "like" ? "👍" : "👎")) : null,
      };
      setState(next);
      onChange && onChange(next);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      // Rollback optimistic update on error
      setState(p);
    } finally { setReacting(false); }
  };

  const loadComments = async () => {
    try {
      const r = await api.get(`/social/posts/${p.id}/comments`);
      setComments(r.data.comments);
    } catch { setComments([]); }
  };

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && comments === null) await loadComments();
  };

  const share = async () => {
    try {
      await api.post(`/social/posts/${p.id}/share`);
      const next = { ...p, shares_count: (p.shares_count || 0) + 1 };
      setState(next);
      onChange && onChange(next);
      const url = `${window.location.origin}/feed?post=${p.id}`;
      try {
        if (navigator.share) {
          await navigator.share({ title: "PJP", text: (p.text || "").slice(0, 140), url });
        } else {
          await navigator.clipboard.writeText(url);
          toast.success("Link copied");
        }
      } catch { /* user cancelled share sheet — counter already updated */ }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/social/posts/${p.id}`);
      onDeleted && onDeleted(p.id);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const saveEdit = async () => {
    const trimmed = editText.trim();
    if (!trimmed) return toast.error("Post text can't be empty");
    if (trimmed === (p.text || "").trim()) {
      setEditing(false);
      return;
    }
    setSavingEdit(true);
    try {
      const r = await api.patch(`/social/posts/${p.id}`, { text: trimmed });
      const next = { ...p, text: r.data.text, edited_at: r.data.edited_at };
      setState(next);
      onChange && onChange(next);
      setEditing(false);
      toast.success("Post updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Edit failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const startEdit = () => {
    setEditText(p.text || "");
    setEditing(true);
    setMenuOpen(false);
  };

  const isAuthor = user?.id === p.author_id;
  const trip = p.checkin?.trip_status;

  return (
    <article
      className="relative rounded-2xl border overflow-hidden transition-all hover:-translate-y-0.5"
      data-testid={`post-${p.id}`}
      style={{
        borderColor: `${finalAccent.c}55`,
        background: `linear-gradient(155deg, ${finalAccent.c}12 0%, rgba(5,8,16,0.94) 45%)`,
        boxShadow: `0 0 40px -22px ${finalAccent.glow}, 0 4px 14px -6px rgba(0,0,0,0.5)`,
      }}
    >
      {/* Top color strip */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[3px] pointer-events-none"
        style={{ background: `linear-gradient(90deg, transparent 0%, ${finalAccent.c} 15%, ${finalAccent.c} 85%, transparent 100%)`, boxShadow: `0 0 12px ${finalAccent.c}` }}
      />
      {/* HUD corner brackets */}
      <span className="absolute top-2 left-2 w-3 h-3 border-t border-l pointer-events-none" style={{ borderColor: `${finalAccent.c}AA` }} />
      <span className="absolute top-2 right-2 w-3 h-3 border-t border-r pointer-events-none" style={{ borderColor: `${finalAccent.c}AA` }} />
      <span className="absolute bottom-2 left-2 w-3 h-3 border-b border-l pointer-events-none" style={{ borderColor: `${finalAccent.c}66` }} />
      <span className="absolute bottom-2 right-2 w-3 h-3 border-b border-r pointer-events-none" style={{ borderColor: `${finalAccent.c}66` }} />
      {/* AOG live pulse — extra corner alert for AOG posts */}
      {isAOG && (
        <span
          aria-hidden
          className="absolute top-3 right-8 mono text-[9px] tracking-widest font-bold px-2 py-0.5 rounded-full"
          style={{ background: "rgba(255,80,80,0.15)", color: "#FF7A7A", border: "1px solid rgba(255,80,80,0.5)", animation: "post-aog-pulse 1.6s ease-in-out infinite" }}
        >
          ● AOG
        </span>
      )}
      <style>{`@keyframes post-aog-pulse { 0%,100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
      {/* Header */}
      <div className="p-4 sm:p-5 pb-3 flex items-start gap-3">
        <Link
          to={`/crew/${p.author_id}`}
          data-testid={`post-author-link-${p.id}`}
          className="flex-shrink-0 hover:opacity-90 transition-opacity"
          aria-label={`View ${author.name || "member"}'s profile`}
        >
          {author.avatar_url ? (
            <img
              src={abs(author.avatar_url)}
              alt=""
              className="h-11 w-11 rounded-full object-cover"
              style={{ border: `2px solid ${finalAccent.c}88`, boxShadow: `0 0 12px -3px ${finalAccent.glow}` }}
            />
          ) : (
            <div
              className="h-11 w-11 rounded-full grid place-items-center mono text-sm font-bold text-white"
              style={{
                background: `linear-gradient(135deg, ${finalAccent.c}55 0%, ${finalAccent.c}15 100%)`,
                border: `2px solid ${finalAccent.c}88`,
                boxShadow: `0 0 12px -3px ${finalAccent.glow}`,
              }}
            >
              {(author.name || "?").split(" ").map((s) => s.charAt(0)).slice(0, 2).join("")}
            </div>
          )}
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <Link
              to={`/crew/${p.author_id}`}
              data-testid="post-author-name"
              className="font-semibold text-white truncate hover:text-cyan transition-colors"
            >
              {author.name || "Member"}
            </Link>
            {author.faa_verified && (
              <span title="FAA verified" className="inline-flex items-center text-avail" data-testid="author-faa-verified">
                <ShieldCheck className="h-3.5 w-3.5" />
              </span>
            )}
            {author.pjp_badge_label && (
              <span className="mono text-[9px] px-1.5 py-0.5 rounded border border-white/15 text-white/60 tracking-wider">{author.pjp_badge_label}</span>
            )}
          </div>
          <div className="mono text-[11px] text-white/45 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <span>{relTime(p.created_at)}</span>
            {p.edited_at && <><span>·</span><span className="italic">edited</span></>}
            {author.location && <><span>·</span><span className="truncate">{author.location}</span></>}
          </div>
        </div>
        {isAuthor && (
          <div className="relative">
            <button onClick={() => setMenuOpen((v) => !v)} className="p-2 -mr-2 text-white/50 hover:text-white" data-testid={`post-menu-${p.id}`}>
              <MoreHorizontal className="h-4 w-4" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-1 bg-obsidian border border-white/15 rounded-lg py-1 z-10 shadow-xl min-w-[140px]">
                <button onClick={startEdit} className="w-full text-left px-4 py-2 text-sm text-white/85 hover:bg-white/[0.04] flex items-center gap-2" data-testid={`post-edit-${p.id}`}>
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={del} className="w-full text-left px-4 py-2 text-sm text-[#EB5757] hover:bg-white/[0.04] flex items-center gap-2" data-testid={`post-delete-${p.id}`}>
                  <Trash2 className="h-3.5 w-3.5" /> Delete
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Check-in / Location chips */}
      {(p.checkin || p.location_icao) && (
        <div className="px-4 sm:px-5 pb-2 flex flex-wrap gap-1.5">
          {p.location_icao && (
            <Link to={`/airports/${p.location_icao}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-sky/10 border border-sky/40 text-sky text-[11px] mono hover:bg-sky/15" data-testid={`post-location-${p.location_icao}`}>
              <MapPin className="h-3 w-3" /> {p.location_icao}
            </Link>
          )}
          {p.checkin?.tail_number && (
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] mono ${p.checkin.verified ? "bg-avail/10 border-avail/40 text-avail" : "bg-white/[0.04] border-white/15 text-white/70"}`} data-testid={`post-tail-${p.checkin.tail_number}`}>
              <Plane className="h-3 w-3" /> {p.checkin.tail_number}
              {p.checkin.verified && <CheckCircle2 className="h-3 w-3" />}
              {p.checkin.aircraft_type && <span className="opacity-80">· {p.checkin.aircraft_type}</span>}
            </span>
          )}
          {trip && (
            <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] mono tracking-wide ${TRIP_TONE[trip] || "bg-white/[0.04] border-white/15 text-white/70"}`} data-testid={`post-trip-${trip}`}>
              {TRIP_LABEL[trip] || trip}
            </span>
          )}
        </div>
      )}

      {/* Body */}
      {editing ? (
        <div className="px-4 sm:px-5 pb-3" data-testid={`post-edit-form-${p.id}`}>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={Math.max(3, Math.min(10, editText.split("\n").length + 1))}
            className="w-full bg-white/[0.03] border border-cyan/30 rounded-lg px-3 py-2 text-white/90 focus:outline-none focus:border-cyan resize-none"
            style={{ fontSize: 15, lineHeight: 1.55 }}
            autoFocus
            data-testid={`post-edit-textarea-${p.id}`}
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") saveEdit();
              if (e.key === "Escape") { setEditing(false); setEditText(p.text || ""); }
            }}
          />
          <div className="flex items-center justify-between mt-2">
            <span className="mono text-[10px] text-white/40 tracking-widest uppercase">⌘/Ctrl + Enter to save · Esc to cancel</span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => { setEditing(false); setEditText(p.text || ""); }}
                data-testid={`post-edit-cancel-${p.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 text-white/70 text-sm hover:border-white/30 hover:text-white"
              >
                <XIcon className="h-3.5 w-3.5" /> Cancel
              </button>
              <button
                type="button"
                onClick={saveEdit}
                disabled={savingEdit}
                data-testid={`post-edit-save-${p.id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-cyan text-obsidian text-sm font-semibold hover:brightness-110 disabled:opacity-60"
              >
                {savingEdit ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><CheckIcon className="h-3.5 w-3.5" /> Save</>}
              </button>
            </div>
          </div>
        </div>
      ) : p.text && (
        <div className="px-4 sm:px-5 pb-3 text-white/90 whitespace-pre-wrap break-words" data-testid={`post-text-${p.id}`} style={{ fontSize: 15, lineHeight: 1.55 }}>
          {linkifyText(p.text)}
        </div>
      )}

      {/* Media */}
      {p.media && p.media.length > 0 && (
        <div className={`grid gap-0.5 ${p.media.length === 1 ? "grid-cols-1" : "grid-cols-2"} bg-black`} data-testid={`post-media-${p.id}`}>
          {p.media.slice(0, 4).map((m, i) => (
            <div key={m.file_id || i} className="relative bg-black">
              {m.kind === "video" ? (
                <video src={abs(m.url)} controls playsInline preload="metadata" className="w-full max-h-[520px] object-contain bg-black" />
              ) : (
                <img src={abs(m.url)} alt="" loading="lazy" className="w-full max-h-[520px] object-cover" />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Counters */}
      {(p.likes_count > 0 || p.dislikes_count > 0 || p.comments_count > 0 || p.shares_count > 0) && (
        <div className="px-4 sm:px-5 py-2 flex items-center gap-3 text-[12px] mono text-white/50 border-t border-white/[0.05]">
          {p.likes_count > 0 && <span>{p.likes_count} likes</span>}
          {p.dislikes_count > 0 && <span>{p.dislikes_count} dislikes</span>}
          {p.comments_count > 0 && <button onClick={toggleComments} className="hover:text-white">{p.comments_count} comments</button>}
          {p.shares_count > 0 && <span>{p.shares_count} shares</span>}
        </div>
      )}

      {/* Actions */}
      <div className="grid grid-cols-4 border-t border-white/[0.06]">
        <ReactionButton
          testId={`post-like-${p.id}`}
          activeReaction={p.my_reaction === "like" ? (p.my_reaction_emoji || "👍") : null}
          activeColor="#4B9CFF"
          defaultIcon={ThumbsUp}
          defaultLabel="Like"
          reactions={LIKE_REACTIONS}
          onPick={(emoji) => react("like", emoji)}
        />
        <ReactionButton
          testId={`post-dislike-${p.id}`}
          activeReaction={p.my_reaction === "dislike" ? (p.my_reaction_emoji || "👎") : null}
          activeColor="#EB5757"
          defaultIcon={ThumbsDown}
          defaultLabel="Dislike"
          reactions={DISLIKE_REACTIONS}
          onPick={(emoji) => react("dislike", emoji)}
        />
        <ActionButton testId={`post-comment-${p.id}`} onClick={toggleComments} icon={MessageCircle} label="Comment" />
        <ActionButton testId={`post-share-${p.id}`} onClick={share} icon={Share2} label="Share" />
      </div>

      {/* Comments */}
      {showComments && (
        <div className="p-4 sm:p-5 border-t border-white/[0.06] bg-black/25" data-testid={`comments-${p.id}`}>
          {comments === null ? (
            <div className="text-white/50 text-sm">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="text-white/40 text-sm">No comments yet. Be the first.</div>
          ) : (
            <div className="space-y-3 mb-3">
              {comments.map((c) => (
                <Comment key={c.id} c={c} abs={abs} onDeleted={(cid) => setComments((prev) => prev.filter(x => x.id !== cid))} onDecrement={() => { const next = { ...p, comments_count: Math.max(0, (p.comments_count || 0) - 1) }; setState(next); onChange && onChange(next); }} />
              ))}
            </div>
          )}
          <CommentComposer
            postId={p.id}
            onCreated={(c) => {
              setComments((prev) => [...(prev || []), c]);
              const next = { ...p, comments_count: (p.comments_count || 0) + 1 };
              setState(next);
              onChange && onChange(next);
            }}
          />
        </div>
      )}
    </article>
  );
}

function ActionButton({ icon: Icon, label, onClick, active, activeColor = "#4B9CFF", testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="inline-flex items-center justify-center gap-2 py-3 text-sm font-medium tracking-tight transition-colors hover:bg-white/[0.04] min-h-[48px]"
      style={{ color: active ? activeColor : "rgba(255,255,255,0.7)" }}
    >
      <Icon className="h-4 w-4" style={active ? { fill: activeColor } : {}} />
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

// ─── Reaction sets ──────────────────────────────────────────────────────────
const LIKE_REACTIONS = [
  { emoji: "👍",  label: "Like",       color: "#4B9CFF" },
  { emoji: "❤️",  label: "Love",       color: "#FF6B9D" },
  { emoji: "😂",  label: "Haha",       color: "#F4C443" },
  { emoji: "🤯",  label: "Mind-blown", color: "#C99BFF" },
  { emoji: "🔥",  label: "Fire",       color: "#FF7A3D" },
  { emoji: "✈️",  label: "Aviator",    color: "#5FE895" },
];
const DISLIKE_REACTIONS = [
  { emoji: "👎", label: "Dislike",  color: "#EB5757" },
  { emoji: "😡", label: "Mad",      color: "#FF4D4D" },
  { emoji: "💩", label: "Nope",     color: "#B08050" },
  { emoji: "🤡", label: "Clown",    color: "#FF6B9D" },
  { emoji: "🥱", label: "Boring",   color: "#95A5C7" },
  { emoji: "🚫", label: "Hard no",  color: "#FF4D4D" },
];

// ─── ReactionButton — action button with a hover/long-press reaction picker
function ReactionButton({ testId, activeReaction, activeColor, defaultIcon: DefaultIcon, defaultLabel, reactions, onPick }) {
  const [open, setOpen] = useState(false);
  const hoverTimer = useRef(null);
  const closeTimer = useRef(null);

  const onEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    hoverTimer.current = setTimeout(() => setOpen(true), 220);
  };
  const onLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 180);
  };

  // Long-press for touch — 380ms threshold.
  const pressTimer = useRef(null);
  const onTouchStart = () => {
    pressTimer.current = setTimeout(() => setOpen(true), 380);
  };
  const onTouchEnd = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
  };

  const pick = (emoji) => {
    onPick(emoji);
    setOpen(false);
  };
  // Bare-click toggles the default reaction on/off.
  const bareClick = () => {
    if (open) return; // ignore if picker is open (it handles clicks)
    onPick(reactions[0].emoji);
  };

  return (
    <div className="relative flex-1" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {/* Floating picker */}
      {open && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 flex items-center gap-1 px-2 py-1.5 rounded-full backdrop-blur z-30"
          style={{
            background: "rgba(10,15,26,0.92)",
            border: "1px solid rgba(75,156,255,0.35)",
            boxShadow: "0 8px 30px -6px rgba(0,0,0,0.7), 0 0 20px -8px rgba(75,156,255,0.4)",
            animation: "reaction-in 180ms ease-out",
          }}
          data-testid={`${testId}-picker`}
          onMouseEnter={() => { if (closeTimer.current) clearTimeout(closeTimer.current); }}
        >
          {reactions.map((r) => (
            <button
              key={r.emoji}
              type="button"
              onClick={() => pick(r.emoji)}
              title={r.label}
              data-testid={`${testId}-picker-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="relative w-9 h-9 grid place-items-center rounded-full transition-transform hover:scale-125 hover:-translate-y-1"
              style={{ background: `${r.color}18`, border: `1px solid ${r.color}55` }}
            >
              <span className="text-lg leading-none">{r.emoji}</span>
            </button>
          ))}
        </div>
      )}
      <style>{`@keyframes reaction-in { 0% { opacity: 0; transform: translate(-50%, 4px); } 100% { opacity: 1; transform: translate(-50%, 0); } }`}</style>
      <button
        type="button"
        onClick={bareClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid={testId}
        className="w-full inline-flex items-center justify-center gap-2 py-3 text-sm font-medium tracking-tight transition-colors hover:bg-white/[0.04] min-h-[48px]"
        style={{ color: activeReaction ? activeColor : "rgba(255,255,255,0.7)" }}
      >
        {activeReaction ? (
          <>
            <span className="text-base leading-none">{activeReaction}</span>
            <span className="hidden sm:inline" style={{ color: activeColor }}>{defaultLabel}</span>
          </>
        ) : (
          <>
            <DefaultIcon className="h-4 w-4" />
            <span className="hidden sm:inline">{defaultLabel}</span>
          </>
        )}
      </button>
    </div>
  );
}

function Comment({ c, abs, onDeleted, onDecrement }) {
  const { user } = useAuth();
  const isAuthor = user?.id === c.author_id;
  // Client-side comment likes with a reaction picker. Persists via localStorage
  // so a page refresh keeps your reaction; server-side comment reactions can
  // be wired later on the same picker payload.
  const storageKey = `pjp_comment_react_${c.id}`;
  const [reaction, setReaction] = useState(() => {
    try { return localStorage.getItem(storageKey) || null; } catch { return null; }
  });
  const [likes, setLikes] = useState(c.likes_count || 0);
  const setReactionAndPersist = (emoji) => {
    if (reaction === emoji) {
      setReaction(null);
      setLikes((n) => Math.max(0, n - 1));
      try { localStorage.removeItem(storageKey); } catch {}
    } else {
      if (!reaction) setLikes((n) => n + 1);
      setReaction(emoji);
      try { localStorage.setItem(storageKey, emoji); } catch {}
    }
  };
  const del = async () => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/social/comments/${c.id}`);
      try { localStorage.removeItem(storageKey); } catch {}
      onDeleted && onDeleted(c.id);
      onDecrement && onDecrement();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  return (
    <div className="flex gap-2" data-testid={`comment-${c.id}`}>
      <Link
        to={`/crew/${c.author_id}`}
        className="flex-shrink-0 hover:opacity-90 transition-opacity"
        data-testid={`comment-author-link-${c.id}`}
        aria-label={`View ${c.author?.name || "member"}'s profile`}
      >
        {c.author?.avatar_url ? (
          <img src={abs(c.author.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover border border-white/15" />
        ) : (
          <div className="h-8 w-8 rounded-full bg-white/10 border border-white/15 grid place-items-center mono text-[10px] font-bold text-white">
            {(c.author?.name || "?").split(" ").map((s) => s.charAt(0)).slice(0, 2).join("")}
          </div>
        )}
      </Link>
      <div className="flex-1 min-w-0">
        <div className="relative bg-white/[0.04] rounded-2xl px-3.5 py-2">
          <div className="flex items-center justify-between gap-2">
            <Link
              to={`/crew/${c.author_id}`}
              data-testid={`comment-author-name-${c.id}`}
              className="text-[13px] font-semibold text-white truncate hover:text-cyan transition-colors"
            >
              {c.author?.name || "Member"}
            </Link>
            {isAuthor && (
              <button onClick={del} className="text-white/40 hover:text-[#EB5757] text-[11px]" data-testid={`comment-delete-${c.id}`}>Delete</button>
            )}
          </div>
          <div className="text-[14px] text-white/85 whitespace-pre-wrap break-words">{linkifyText(c.text)}</div>
          {/* Reaction chip badge floating on the corner */}
          {reaction && likes > 0 && (
            <div className="absolute -bottom-2 right-3 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold" style={{ background: "#0A0F1A", border: "1px solid rgba(75,156,255,0.4)", color: "#4B9CFF" }}>
              <span className="text-[12px] leading-none">{reaction}</span>
              <span>{likes}</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 ml-1">
          <div className="mono text-[10px] text-white/40">{relTime(c.created_at)}</div>
          <CommentLikeButton
            active={reaction}
            testId={`comment-like-${c.id}`}
            onPick={setReactionAndPersist}
          />
        </div>
      </div>
    </div>
  );
}

// Compact "Like" toggle on each comment with a hover/long-press reaction picker.
function CommentLikeButton({ active, testId, onPick }) {
  const [open, setOpen] = useState(false);
  const timerIn = useRef(null);
  const timerOut = useRef(null);
  const onEnter = () => {
    if (timerOut.current) clearTimeout(timerOut.current);
    timerIn.current = setTimeout(() => setOpen(true), 220);
  };
  const onLeave = () => {
    if (timerIn.current) clearTimeout(timerIn.current);
    timerOut.current = setTimeout(() => setOpen(false), 160);
  };
  const pressTimer = useRef(null);
  const onTouchStart = () => { pressTimer.current = setTimeout(() => setOpen(true), 380); };
  const onTouchEnd = () => { if (pressTimer.current) clearTimeout(pressTimer.current); };

  const bareClick = () => {
    if (open) return;
    onPick(active || "👍");
  };
  return (
    <div className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      {open && (
        <div
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 flex items-center gap-0.5 px-1.5 py-1 rounded-full backdrop-blur z-30"
          style={{
            background: "rgba(10,15,26,0.94)",
            border: "1px solid rgba(75,156,255,0.35)",
            boxShadow: "0 6px 20px -6px rgba(0,0,0,0.7)",
          }}
          data-testid={`${testId}-picker`}
          onMouseEnter={() => { if (timerOut.current) clearTimeout(timerOut.current); }}
        >
          {LIKE_REACTIONS.map((r) => (
            <button
              key={r.emoji}
              type="button"
              onClick={() => { onPick(r.emoji); setOpen(false); }}
              title={r.label}
              data-testid={`${testId}-picker-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="w-7 h-7 grid place-items-center rounded-full hover:scale-125 transition-transform"
              style={{ background: `${r.color}18` }}
            >
              <span className="text-[15px] leading-none">{r.emoji}</span>
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={bareClick}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid={testId}
        className="mono text-[10.5px] tracking-wider font-semibold px-2 py-0.5 rounded transition-colors hover:bg-white/5"
        style={{ color: active ? "#4B9CFF" : "rgba(255,255,255,0.5)" }}
      >
        {active ? <><span className="mr-1">{active}</span>Liked</> : "Like"}
      </button>
    </div>
  );
}
